import { motion } from "framer-motion";
import { ArrowLeft, Bell, Sparkles, type LucideIcon } from "lucide-react";

import ModuleCard from "@/components/painel/ModuleCard";
import { Button } from "@/components/ui/button";
import { useNavigationHelpers } from "@/lib/navigation-helpers";

interface Funcionalidade {
  title: string;
  description: string;
  href: string;
  icon: LucideIcon;
}

const FUNCIONALIDADES: Funcionalidade[] = [
  {
    title: "Teste de Notificação",
    description: "Envie um e-mail ou WhatsApp de teste e veja o resultado por destinatário.",
    href: "/admin/servicos-externos/notificacao-teste",
    icon: Bell,
  },
];

// Fundo reutilizado do SubMenuDashboard.
const DashboardBackground = () => (
  <div className="absolute inset-0 -z-10 h-full w-full overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] opacity-60 [background-size:20px_20px] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_0%,#000_70%,transparent_100%)] dark:bg-[radial-gradient(#ffffff15_1px,transparent_1px)]" />
    <div className="pointer-events-none absolute left-1/2 top-0 h-[300px] w-[800px] -translate-x-1/2 rounded-full bg-primary/10 blur-[100px]" />
  </div>
);

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.04 } },
};

export default function ServicosExternosPage() {
  const { goTo } = useNavigationHelpers();

  return (
    <div className="relative min-h-screen w-full">
      <DashboardBackground />

      <div className="relative mx-auto w-full px-4 pb-4 pt-4 md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mb-12 space-y-4"
        >
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => goTo("/")}
              className="text-muted-foreground transition-colors hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar para Módulos
            </Button>

            <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              <Sparkles className="h-3 w-3" />
              <span>Serviços Externos</span>
            </div>
          </div>

          <h1 className="text-4xl font-bold tracking-tight text-foreground md:text-5xl">
            Acesso a{" "}
            <span className="bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
              Serviços Externos
            </span>
          </h1>
          <p className="max-w-2xl text-lg leading-relaxed text-muted-foreground">
            Selecione uma das ferramentas disponíveis para testar as integrações externas.
          </p>
        </motion.div>

        {/* Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          {FUNCIONALIDADES.map((f, index) => (
            <ModuleCard
              key={f.href}
              index={index}
              title={f.title}
              description={f.description}
              icon={<f.icon />}
              href={f.href}
            />
          ))}
        </motion.div>
      </div>
    </div>
  );
}
